import { useEffect, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { PhoneInput } from 'react-international-phone';
import * as Yup from 'yup';
import * as yup from 'yup';
import YupPassword from 'yup-password';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { debounce } from 'lodash';
import { Box, Button, Checkbox, FormControl, FormHelperText, IconButton, InputLabel, MenuItem, Select, Tooltip, Autocomplete } from '@mui/material';
import { Info } from '@mui/icons-material';
import { Form, Formik, FormikProps } from 'formik';

// Style
import 'react-international-phone/style.css';

// Hooks and services
import authService from '../../../services/auth.service';
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';
import { getCustomerTypes, getTerms } from '../../../services/setting.service';
import { searchBasicStations } from '../../../services/station.service';

// Types
import { RegistrationRequest } from '../../../requestModels/registrationRequest';
import { ValidationErrorResponse } from '../../../utils/ValidationErrorResponse';
import { BasicStation } from '../../../types/Station';

// Common
import Footer from '../../base/layout/Footer';
import SiteBranding from '../../base/branding/sitebranding';
import TextField from '../../common/forms/TextField';

// Styles
import './Auth.scss';

YupPassword(yup);

const Register = () => {
  const { notifyMessage } = useNotifyMessage();
  const navigate = useNavigate();

  const [phoneIsValid, setPhoneIsValid] = useState(false);
  const [isPhoneTouched, setIsPhoneTouched] = useState(false);
  const [customerTypes, setCustomerTypes] = useState<string[]>([]);
  const [terms, setTerms] = useState<string>('Loading...');
  const [stations, setStations] = useState<BasicStation[]>([]);

  const phoneUtil = PhoneNumberUtil.getInstance();
  const isPhoneValid = (phone: string) => {
    try {
      return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    getCustomerTypes()
      .then((r) => setCustomerTypes(JSON.parse(r.value ?? [])))
      .catch(console.error);

    getTerms()
      .then((r) => setTerms(r.value ?? ''))
      .catch(console.error);
  }, []);

  const handleSubmit = async (values: RegistrationRequest) => {
    const response = await authService.register(values as RegistrationRequest);

    if (response.status == 200) {
      navigate('/thank-you-for-registering', { state: { email: values.email } });
    }

    if (response.status == 400) {
      const errorResponse = response.data as ValidationErrorResponse;
      let lowerCaseFirst: string;
      const errors: Record<string, unknown> = {};

      if (errorResponse.errors) {
        for (const a in errorResponse.errors) {
          lowerCaseFirst = a.charAt(0).toLowerCase() + a.slice(1);
          errors[lowerCaseFirst] = errorResponse.errors[a];
        }
        if (formRef && errors) {
          formRef.setErrors(errors);
        }
      }
    }

    if (response.status == 409) {
      notifyMessage(response.data.toString(), 'error');
    }
  };

  const airlineInfo =
    'Please provide an affiliation to an airline (if any). For example if you are an airline representative or a ground handling agent. This information is used to provide you with the correct data scope for your account.';

  const validationSchema = Yup.object<RegistrationRequest>({
    firstname: Yup.string().required('First name is required'),
    lastname: Yup.string().required('Last name is required'),
    company: Yup.string().required('Company is required'),
    customerType: Yup.string().required('Customer type is required').oneOf(customerTypes, 'Choose a customer type from the list'),
    location: Yup.string().length(3, 'Must be exactly 3 characters').required('IATA station code is required'),
    email: Yup.string().required('Email address is required').email('Email is not a valid email address'),
    password: Yup.string().password().required('Password is required'),
    passwordConfirmation: Yup.string()
      .required('Please re-type the password')
      .oneOf([Yup.ref('password'), ''], 'Passwords must match'),
    agreedWithTerms: Yup.boolean().required('Please agree with the terms and conditions'),
  });

  const initialValues: RegistrationRequest = {
    firstname: '',
    lastname: '',
    company: '',
    customerType: '',
    location: '',
    airlines: '',
    email: '',
    password: '',
    passwordConfirmation: '',
    phone: '',
    agreedWithTerms: false,
  } as RegistrationRequest;

  let formRef: FormikProps<RegistrationRequest>;

  async function handleLocationInputChange(_: React.SyntheticEvent, value: string) {
    formRef.setFieldValue('location', value?.toUpperCase() ?? '');
    if (value) await debouncedFetchOptions(value);
  }

  const debouncedFetchOptions = debounce(async (query: string) => {
    await fetchStations(query);
  }, 200);

  const fetchStations = async (query: string) => {
    await searchBasicStations(query).then((res) => setStations(res));
  };

  return (
    <div className='auth auth--centered'>
      <div className='auth-brand' style={{ marginBottom: 20 }}>
        <SiteBranding />
      </div>

      <div className='auth-card auth-card--wide'>
        <h1 className='auth-title'>Create your account</h1>
        <p className='auth-subtitle'>All registrations require approval before access is granted</p>

        <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
          {(props: FormikProps<RegistrationRequest>) => (
            <Form>
              {props ? (formRef = props) && '' : ''}

              <div className='auth-form'>
                <div className='auth-register__section-title'>Identity</div>

                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                  <FormControl fullWidth>
                    <TextField required name='firstname' label='First name' />
                  </FormControl>
                  <FormControl fullWidth>
                    <TextField required name='lastname' label='Last name' />
                  </FormControl>
                </Box>

                <FormControl fullWidth>
                  <TextField required name='company' label='Company' />
                </FormControl>

                <FormControl fullWidth size='small'>
                  <InputLabel id='customer-type-label' required shrink>
                    Customer Type
                  </InputLabel>
                  <Select
                    required
                    labelId='customer-type-label'
                    label='Customer Type'
                    defaultValue=''
                    name='customerType'
                    onChange={(e) => props.setFieldValue('customerType', e.target.value ?? '')}
                    size='small'
                    notched
                  >
                    {customerTypes.map((x) => (
                      <MenuItem key={x} value={x}>
                        {x}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error={props.touched?.customerType && Boolean(props.errors?.customerType)}>
                    {props.touched?.customerType && props.errors?.customerType}
                  </FormHelperText>
                </FormControl>

                <FormControl fullWidth>
                  <Autocomplete
                    freeSolo
                    filterOptions={(x) => x}
                    disablePortal
                    options={stations}
                    renderOption={(renderProps, station) => (
                      <li {...renderProps} key={station.iataCode}>
                        <strong>{station.iataCode}</strong>
                        <span style={{ marginLeft: 8, color: '#6B7280' }}>{station.name}</span>
                      </li>
                    )}
                    getOptionLabel={(option: any) => option?.iataCode ?? option}
                    onInputChange={handleLocationInputChange}
                    includeInputInList
                    renderInput={(params) => <TextField {...params} label='Airport (IATA)' name='location' required />}
                  />
                </FormControl>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <FormControl fullWidth>
                    <TextField label='Airline(s)' name='airlines' />
                  </FormControl>
                  <Tooltip title={airlineInfo}>
                    <IconButton aria-label='airlines' size='small' sx={{ mt: 0.5 }}>
                      <Info fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Box>

                <div className='auth-register__section-title'>Contact Information</div>

                <FormControl fullWidth>
                  <TextField required label='Email address' type='email' name='email' />
                </FormControl>

                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                  <FormControl fullWidth>
                    <TextField required label='Password' autoComplete='new-password' type='password' name='password' />
                  </FormControl>
                  <FormControl fullWidth>
                    <TextField required label='Confirm password' autoComplete='new-password' type='password' name='passwordConfirmation' />
                  </FormControl>
                </Box>

                <div className='auth-register__phone-wrap'>
                  <InputLabel shrink sx={{ mb: 0.5 }}>
                    Phone number
                  </InputLabel>
                  <PhoneInput
                    name='phone'
                    onChange={(value) => {
                      setPhoneIsValid(isPhoneValid(value));
                      props.setFieldValue('phone', value);
                    }}
                    onBlur={() => setIsPhoneTouched(true)}
                  />
                  <FormHelperText error={!phoneIsValid}>{isPhoneTouched && !phoneIsValid && 'Phone is not valid'}</FormHelperText>
                </div>

                <div className='auth-register__section-title'>Terms</div>

                <FormControl fullWidth className='auth-register__terms-box'>
                  <TextField label='Terms and Conditions' disabled fullWidth name='terms' multiline minRows={5} maxRows={10} value={terms} />
                </FormControl>

                <label className='auth-register__agree-row'>
                  <Checkbox onChange={(e) => props.setFieldValue('agreedWithTerms', e.target.checked)} name='agreedWithTerms' required size='small' />
                  <span>I agree with the terms and conditions</span>
                </label>
                <FormHelperText error={props.touched?.agreedWithTerms && Boolean(props.errors?.agreedWithTerms)}>
                  {props.touched?.agreedWithTerms && props.errors?.agreedWithTerms}
                </FormHelperText>

                <Button
                  type='submit'
                  color='primary'
                  variant='contained'
                  className='auth-btn auth-btn--primary'
                  disableElevation
                  fullWidth
                  sx={{ mt: 1 }}
                >
                  Sign up
                </Button>

                <div className='auth-switch'>
                  Already have an account?
                  <RouterLink to='/login'>Sign in</RouterLink>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      <Footer />
    </div>
  );
};

export default Register;
